// src/pages/FinancialReportsPage.js
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, DatePicker, Select, Table } from "antd";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { CardTitle, SectionCard, StatStrip, fmtMoney, tablePagination, useFlatPage } from "../components/ui";

const { RangePicker } = DatePicker;
const CURRENCY = "BWP";

const monthName = (n) => new Date(2000, n - 1, 1).toLocaleString("en-GB", { month: "long" });

const FinancialReportsPage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [report, setReport] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [range, setRange] = useState([]);
  const [roomId, setRoomId] = useState("all");

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...(range?.[0] && { startDate: range[0].toISOString() }),
        ...(range?.[1] && { endDate: range[1].toISOString() }),
        ...(roomId !== "all" && { roomId }),
      };
      const { data } = await axiosInstance.get("/FinancialReport/GetReport", { params });
      setReport(data);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load the report.");
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hotelId) return;
    fetchReport();
    axiosInstance
      .get(`/Rooms/all/${hotelId}`)
      .then((r) => setRooms(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRooms([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  const rows = useMemo(
    () => (report?.monthlyBreakdown || []).map((m) => ({ ...m, key: m.month })),
    [report]
  );

  // The API's field names are older than what they hold: "revenue" is the invoiced total,
  // "expenses" is the amount already paid, "profit" is what is still outstanding.
  const columns = [
    { title: "Month", dataIndex: "month", key: "month", render: (m) => <span className="hms-cell-main">{monthName(m)}</span> },
    { title: "Invoiced", dataIndex: "revenue", key: "revenue", align: "right", render: (v) => fmtMoney(CURRENCY, v) },
    { title: "Collected", dataIndex: "expenses", key: "expenses", align: "right", render: (v) => fmtMoney(CURRENCY, v) },
    {
      title: "Outstanding",
      dataIndex: "profit",
      key: "profit",
      align: "right",
      render: (v) => <span className={Number(v) > 0 ? "hms-cell-main" : "hms-muted"}>{fmtMoney(CURRENCY, v)}</span>,
    },
  ];

  return (
    <div className="hms-page fr-page">
      <div className="hms-stack">
        <SectionCard
          flush
          title="Financial report"
          extra={
            <div className="hms-form-actions" style={{ marginTop: 0, flexWrap: "wrap" }}>
              <Select
                style={{ width: 220 }}
                value={roomId}
                onChange={setRoomId}
                options={[
                  { value: "all", label: "All rooms" },
                  ...rooms.map((r) => ({ value: r.roomId, label: `${r.roomNumber}${r.roomTypeName ? ` - ${r.roomTypeName}` : ""}` })),
                ]}
              />
              <RangePicker value={range} onChange={(v) => setRange(v || [])} />
              <Button type="primary" loading={loading} onClick={fetchReport}>
                Apply filters
              </Button>
            </div>
          }
        >
          {error ? (
            <Alert style={{ margin: 16 }} type="error" showIcon title="Report unavailable" description={error} />
          ) : (
            <StatStrip
              items={[
                { label: "Invoiced", value: fmtMoney(CURRENCY, report?.totalRevenue ?? 0) },
                { label: "Collected", value: fmtMoney(CURRENCY, report?.totalExpenses ?? 0), tone: "good" },
                {
                  label: "Outstanding",
                  value: fmtMoney(CURRENCY, report?.netProfit ?? 0),
                  tone: Number(report?.netProfit) > 0 ? "warn" : "good",
                },
              ]}
            />
          )}
        </SectionCard>

        <SectionCard flush title={<CardTitle count={rows.length}>Monthly breakdown</CardTitle>}>
          <Table
            className="hms-table"
            columns={columns}
            dataSource={rows}
            loading={loading}
            pagination={tablePagination("month", { hideOnSinglePage: true })}
            locale={{ emptyText: <div className="hms-empty">No invoices for these filters</div> }}
          />
        </SectionCard>
      </div>
    </div>
  );
};

export default FinancialReportsPage;
